/* ============================================
   WORK PAGE
   The Creative Orchestra - GEMINI Framework
   Dynamic work detail page
   ============================================ */

import { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import {
  WorkHero,
  WorkSummary,
  WorkLinks,
  ImageGallery,
  AudioPlayer,
  YouTubeEmbed,
  PDFViewer,
  RelatedWorks,
  NextWorkNav,
} from '@components/work';
import { ScrollToTopButton } from '@components/common';
import { works, getWorkById } from '@data';
import { useRouteTheme } from '@hooks/useWorkTheme';
import { SITE_INFO } from '@utils/constants';

/**
 * Work Page Component
 * Dynamic page for displaying individual work details
 */
const WorkPage = () => {
  const { workId } = useParams();
  const navigate = useNavigate();

  // Apply work theme
  const { theme, isWorkTheme } = useRouteTheme(workId);

  // Get work data
  const work = useMemo(() => getWorkById(workId), [workId]);

  // Set page title
  useEffect(() => {
    if (work) {
      document.title = `${work.name} | ${SITE_INFO.title}`;
    } else {
      document.title = `Work Not Found | ${SITE_INFO.title}`;
    }
  }, [work]);

  // Handle work not found
  if (!work) {
    return <WorkNotFound workId={workId} />;
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <WorkHero work={work} />

      {/* Links Section */}
      <WorkLinks links={work.links} workName={work.name} />

      {/* Summary & Details */}
      <WorkSummary work={work} />

      {/* Image Gallery */}
      {work.gallery && work.gallery.length > 0 && (
        <ImageGallery 
          images={work.gallery} 
          workId={work.id}
          title={`${work.name} Gallery`}
        />
      )}

      {/* Audio Player */}
      {work.audio && (
        <AudioPlayer
          audio={work.audio}
          workName={work.name}
        />
      )}

      {/* YouTube Video */}
      {work.youtubeUrl && (
        <YouTubeEmbed
          youtubeUrl={work.youtubeUrl}
          workName={work.name}
        />
      )}

      {/* PDF Viewer (if paper exists) */}
      {work.links?.paper && (
        <PDFViewer
          url={work.links.paper}
          title={`${work.name} Research Paper`}
          workId={work.id}
        />
      )}

      {/* Related Works */}
      <RelatedWorks
        currentWorkId={work.id}
        relatedWorkIds={work.relatedWorks}
      />

      {/* Next Work Navigation */}
      <NextWorkNav currentWorkId={work.id} />

      {/* Scroll to top button */}
      <ScrollToTopButton />
    </div>
  );
};

/**
 * Work Not Found Component
 */
const WorkNotFound = ({ workId }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <motion.div
        className="text-center max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/10
          flex items-center justify-center">
          <AlertCircle className="w-10 h-10 text-red-400" />
        </div>

        <h1 className="text-3xl font-display font-bold text-white mb-4">
          Work Not Found
        </h1>

        <p className="text-gray-400 mb-8">
          The work "{workId}" doesn't exist or may have been moved. 
          Please check the URL or explore our other works.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <motion.button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg
              bg-white/10 text-white hover:bg-white/20 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </motion.button>

          <motion.button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg
              bg-indigo-500 text-white hover:bg-indigo-400 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            View All Works
          </motion.button>
        </div>

        {/* Available works */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <p className="text-sm text-gray-500 mb-4">Available works:</p>
          <div className="flex flex-wrap justify-center gap-2">
            {Object.keys(works).map((id) => (
              <motion.button
                key={id}
                onClick={() => navigate(`/work/${id}`)}
                className="px-3 py-1.5 rounded-lg bg-white/5 text-gray-400
                  hover:bg-white/10 hover:text-white transition-colors text-sm"
                whileHover={{ scale: 1.05 }}
              >
                {works[id].name}
              </motion.button>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default WorkPage;
